class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    const requestId = ++FacetFiltersForm.requestId;
    document.getElementById('BwFilterError')?.remove();
    // Category searches use an alternate template, including after reset/back.
    const categoryView = document.querySelector('.facets-container[data-search-view]')?.dataset.searchView;
    if (categoryView) {
      const params = new URLSearchParams(searchParams);
      params.set('view', categoryView);
      params.set('type', 'product');
      searchParams = params.toString();
    }
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent, requestId);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent, requestId = FacetFiltersForm.requestId) {
    return fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Filter request failed (${response.status})`);
        return response.text();
      })
      .then((html) => {
        if (requestId !== FacetFiltersForm.requestId) {
          updateEvent?.reject(new Error('Filter request superseded by a newer selection'));
          return;
        }
        FacetFiltersForm.renderSection(html, event, updateEvent);
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
      })
      .catch((error) => {
        if (requestId === FacetFiltersForm.requestId) {
          console.error(error);
          FacetFiltersForm.showLoadError();
        }
        updateEvent?.reject(error);
      });
  }

  static showLoadError() {
    document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.add('hidden'));
    document.querySelectorAll('#ProductGridContainer .loading, #ProductCount, #ProductCountDesktop')
      .forEach((element) => element.classList.remove('loading'));
    FacetFiltersForm.toggleActiveFacets(false);
    if (document.getElementById('BwFilterError')) return;
    const message = document.createElement('p');
    message.id = 'BwFilterError';
    message.className = 'bw-filter-error';
    message.setAttribute('role', 'alert');
    message.append('Die Auswahl konnte nicht aktualisiert werden. ');
    const retry = document.createElement('a');
    retry.href = window.location.href;
    retry.textContent = 'Erneut laden';
    message.append(retry);
    document.getElementById('ProductGridContainer').prepend(message);
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  static renderSection(html, event, updateEvent) {
    // Validate before replacing any visible content, e.g. if a preview expires.
    const responseDocument = new DOMParser().parseFromString(html, 'text/html');
    if (!responseDocument.getElementById('ProductGridContainer') || !responseDocument.getElementById('ProductCount')) {
      throw new Error('Filter response did not contain the requested product grid');
    }
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.restoreFilterAccessibility();
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    FacetFiltersForm.syncRequestedFilters(FacetFiltersForm.searchParamsPrev);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static restoreFilterAccessibility() {
    document.querySelectorAll('facet-filters-form .js-filter > summary').forEach((summary) => {
      const details = summary.parentElement;
      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', String(details.open));
      if (summary.nextElementSibling?.id) summary.setAttribute('aria-controls', summary.nextElementSibling.id);
      if (details.dataset.bwAccessibilityBound) return;
      details.dataset.bwAccessibilityBound = 'true';
      details.addEventListener('toggle', () => {
        details.querySelector('summary')?.setAttribute('aria-expanded', String(details.open));
      });
      if (!details.closest('menu-drawer')) details.addEventListener('keyup', onKeyUpEscape);
    });
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  static renderFilters(html, event) {
    const localSearches = new Map(Array.from(document.querySelectorAll('bw-filter-search input'), input => [input.id, input.value]));
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          const parent = document.getElementById(elementToRender.parentElement.id);
          const anchor = parent?.querySelector('.js-filter, .mobile-facets__sort, .mobile-facets__footer');
          if (anchor) anchor.before(elementToRender);
          else parent?.append(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
    document.querySelectorAll('bw-filter-search').forEach(search => {
      const input = search.querySelector('input');
      if (input && localSearches.has(input.id)) input.value = localSearches.get(input.id);
      search.filter?.();
    });
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static requestedFilterValues(searchParams) {
    const labels = {
      'filter.v.availability': 'Verfügbarkeit',
      'filter.v.option.größe': 'Größe',
      'filter.v.option.farbe': 'Farbe & Tarnmuster',
      'filter.p.vendor': 'Marke',
      'filter.p.product_type': 'Produkttyp',
    };
    const params = new URLSearchParams(searchParams);
    return Array.from(params).filter(([key, value]) => labels[key] && value !== '')
      .map(([key, value]) => ({key, value, label: labels[key]}));
  }

  static syncRequestedFilters(searchParams) {
    // Shopify may omit active option values when a combination has zero matches.
    // Keep those selections visible/removable, including on a shared filtered URL.
    const params = new URLSearchParams(searchParams);
    const values = FacetFiltersForm.requestedFilterValues(searchParams);
    document.querySelectorAll('[data-bw-filter-fallback]').forEach(element => element.remove());
    document.querySelectorAll('#FacetFiltersForm, #FacetFiltersFormMobile').forEach(form => {
      values.forEach(({key, value}) => {
        const represented = Array.from(form.querySelectorAll('input')).some(input =>
          input.name === key && input.value === value && (input.checked || input.type === 'hidden'));
        if (represented) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        input.setAttribute('data-bw-filter-fallback', '');
        form.append(input);
      });
    });
    document.querySelectorAll('.active-facets-mobile, .active-facets-desktop').forEach(container => {
      values.forEach(({key, value, label}) => {
        const represented = Array.from(container.querySelectorAll('facet-remove a.active-facets__button')).some(link => {
          const removeParams = new URL(link.href, window.location.href).searchParams;
          return !removeParams.getAll(key).includes(value) && params.getAll(key).includes(value);
        });
        if (represented) return;
        const remaining = params.getAll(key).filter(item => item !== value);
        const removeParams = new URLSearchParams(params);
        removeParams.delete(key);
        remaining.forEach(item => removeParams.append(key, item));
        const template = document.createElement('template');
        template.innerHTML = '<facet-remove data-bw-filter-fallback><a class="active-facets__button active-facets__button--light"><span class="active-facets__button-inner button button--tertiary"><span data-bw-filter-text></span><span aria-hidden="true"> ×</span><span class="visually-hidden"> Filter entfernen</span></span></a></facet-remove>';
        template.content.querySelector('a').href = `${window.location.pathname}?${removeParams}`;
        const displayValue = key === 'filter.v.availability' ? (value === '1' ? 'Auf Lager' : 'Nicht vorrätig') : value;
        template.content.querySelector('[data-bw-filter-text]').textContent = `${label}: ${displayValue}`;
        container.prepend(template.content);
      });
    });
    const priceActive = ['filter.v.price.gte', 'filter.v.price.lte'].some(key => params.has(key) && params.get(key) !== '');
    const count = values.length + Number(priceActive);
    document.querySelectorAll('.mobile-facets__open').forEach(trigger => {
      let badge = trigger.querySelector('.bw-filter-total');
      if (!count) { badge?.remove(); return; }
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'bw-filter-count bw-filter-total';
        trigger.append(badge);
      }
      badge.textContent = count;
      badge.setAttribute('aria-label', `${count} ${count === 1 ? 'aktiver Filter' : 'aktive Filter'}`);
    });
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    const applyLabel = html.querySelector('[data-bw-filter-apply-label]');
    if (applyLabel) {
      document.querySelectorAll('[data-bw-filter-apply-label]').forEach((label) => {
        label.textContent = applyLabel.textContent;
      });
    }

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const sourceSummary = source.querySelector('.mobile-facets__summary');
    const targetSummary = target.querySelector('.mobile-facets__summary');
    if (sourceSummary && targetSummary) targetSummary.innerHTML = sourceSummary.innerHTML;
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    form.querySelectorAll('price-range input').forEach(input => {
      const value = PriceRange.parsePrice(input.value);
      if (input.value.trim() === '' || !Number.isFinite(value)) formData.delete(input.name);
      else formData.set(input.name, String(value));
    });

    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.requestId = 0;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.rangeMax = this.querySelectorAll('input')[1].getAttribute('data-max');
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    const input = event.currentTarget;
    const previousValue = input.value;
    this.adjustToValidValues(input);
    this.setMinAndMaxValues();
    // The debounced filter request may already have used the typed value.
    // Notify the form when blur corrects it so the grid matches the field.
    if (input.value !== previousValue) input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  onKeyDown(event) {
    if (event.metaKey || event.ctrlKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    const min = PriceRange.parsePrice(minInput.value);
    const max = PriceRange.parsePrice(maxInput.value);
    minInput.setAttribute('data-max', maxInput.value !== '' && Number.isFinite(max) ? max : this.rangeMax);
    maxInput.setAttribute('data-min', minInput.value !== '' && Number.isFinite(min) ? min : 0);
  }

  static parsePrice(value) {
    let normalized = String(value).replace(/\s/g, '');
    if (normalized.includes(',')) normalized = normalized.replace(/\./g, '').replace(',', '.');
    return /^-?\d+(?:\.\d*)?$/.test(normalized) ? Number(normalized) : NaN;
  }

  adjustToValidValues(input) {
    if (input.value.trim() === '') return;
    const value = PriceRange.parsePrice(input.value);
    if (!Number.isFinite(value)) { input.value = ''; return; }
    const min = PriceRange.parsePrice(input.getAttribute('data-min'));
    const max = PriceRange.parsePrice(input.getAttribute('data-max'));
    input.value = String(Math.min(Number.isFinite(max) ? max : Infinity, Math.max(Number.isFinite(min) ? min : 0, value)));
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
window.addEventListener('DOMContentLoaded', () => {
  FacetFiltersForm.syncRequestedFilters(window.location.search.slice(1));
});
